import { type FC } from "react";
import { Label } from "@/components/podkit/forms/Label";
import { Text } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { HelpCircle } from "lucide-react";
import { Tooltip } from "@/components/Tooltip";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
const requirements = [
    {
        label: "OS",
        value: "Modern Linux distribution (Ubuntu 24.04+, CentOS 9)",
    },
    {
        label: "CPU/Arch",
        value: "AMD64 (x86_64) with KVM support",
        details: (
            <div className="flex items-center justify-end gap-1">
                <Text className="text-right text-xs text-content-secondary">Requires virtualization</Text>
                <Tooltip
                    content={
                        <div className="flex max-w-[300px] flex-col gap-2">
                            <Text className="text-content-primary-inverted text-sm">
                                KVM virtualization must be enabled in your BIOS/UEFI settings and supported by your CPU
                                (requires vmx/svm CPU flags).
                            </Text>
                            <Text className="text-content-primary-inverted text-sm">
                                Unsure about virtualization support? Check your CPU flags with:
                            </Text>
                            <CopyableInput
                                className="w-full font-mono text-sm text-content-primary"
                                value='grep -E "vmx|svm" /proc/cpuinfo'
                                copyable
                            />
                            <ExternalLink
                                href="https://www.gitpod.io/docs/flex/runners/linux/requirements"
                                iconSize="sm"
                            >
                                See docs for more information
                            </ExternalLink>
                        </div>
                    }
                    align="end"
                >
                    <HelpCircle className="size-3 shrink-0 text-content-tertiary hover:text-content-primary" />
                </Tooltip>
            </div>
        ),
    },
    {
        label: "Memory",
        value: "2GB+ per environment",
    },
    {
        label: "Storage",
        value: "10GB+ free space",
    },
    {
        label: "Network",
        value: "Internet access",
    },
    {
        label: "Permissions",
        value: "Root access (setup only)",
    },
];

export const RunnerRequirementsTable: FC = () => {
    return (
        <div className="flex flex-col gap-4">
            <Label className="font-semibold">System Requirements</Label>
            <div className="flex flex-col divide-y divide-border-base">
                {requirements.map((req) => (
                    <RequirementRow key={req.label} requirement={req} />
                ))}
            </div>
            <div className="pt-1 text-sm">
                <ExternalLink href="https://www.gitpod.io/docs/flex/runners/linux/requirements" iconSize="sm">
                    How to verify requirements
                </ExternalLink>
            </div>
        </div>
    );
};

type Requirement = {
    label: string;
    value: string;
    details?: React.ReactNode;
};

const RequirementRow: FC<{ requirement: Requirement }> = ({ requirement }) => (
    <div className="py-1.5 first:pt-0 last:pb-0">
        <div className="flex items-baseline justify-between gap-4">
            <Text className="text-sm font-medium text-content-secondary">{requirement.label}</Text>
            <Text className="text-right text-sm">{requirement.value}</Text>
        </div>
        {requirement.details}
    </div>
);
